import { ExternalLink, Bookmark, CheckCircle, ThumbsUp } from 'lucide-react';
import { useState } from 'react';
import { resourceMatching, CoachingResource } from '../services/resource-matching';
import { supabase } from '../services/supabase';
import { logger } from '../services/logger';

interface ResourceCardProps {
  resource: CoachingResource;
  colorScheme?: string;
  showActions?: boolean;
}

export function ResourceCard({ resource, colorScheme = 'gold', showActions = true }: ResourceCardProps) {
  const [isSaved, setIsSaved] = useState(false);
  const [isHelpful, setIsHelpful] = useState<boolean | null>(null);

  const colorClasses: Record<string, { bg: string; border: string; text: string; hover: string }> = {
    gold: {
      bg: 'bg-gold-50',
      border: 'border-gold-200',
      text: 'text-gold-600',
      hover: 'hover:border-gold-400'
    },
    blue: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-600',
      hover: 'hover:border-blue-400'
    },
    green: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      text: 'text-green-600',
      hover: 'hover:border-green-400'
    },
    gray: {
      bg: 'bg-gray-50',
      border: 'border-gray-200',
      text: 'text-gray-600',
      hover: 'hover:border-gray-400'
    },
    orange: {
      bg: 'bg-orange-50',
      border: 'border-orange-200',
      text: 'text-orange-600',
      hover: 'hover:border-orange-400'
    }
  };

  const colors = colorClasses[colorScheme] || colorClasses.gold;

  const handleClick = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await resourceMatching.trackInteraction(user.id, resource.id, 'click');
      }
      window.open(resource.url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      logger.error('Error tracking resource click:', error);
      window.open(resource.url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleSave = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await resourceMatching.trackInteraction(user.id, resource.id, 'save');
        setIsSaved(true);
      }
    } catch (error) {
      logger.error('Error saving resource:', error);
    }
  };

  const handleHelpful = async (helpful: boolean, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await resourceMatching.trackInteraction(
          user.id,
          resource.id,
          helpful ? 'helpful' : 'not_helpful'
        );
        setIsHelpful(helpful);
      }
    } catch (error) {
      logger.error('Error tracking feedback:', error);
    }
  };

  return (
    <div
      onClick={handleClick}
      className={`group relative overflow-hidden rounded-lg border-2 ${colors.border} ${colors.hover} bg-white p-4 transition-all hover:shadow-md cursor-pointer`}
    >
      <div className={`absolute top-0 right-0 w-24 h-24 ${colors.bg} opacity-30 rounded-full -mr-12 -mt-12 group-hover:opacity-50 transition-opacity`} />

      <div className="relative">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className={`font-semibold ${colors.text} group-hover:underline`}>
                {resource.title}
              </h3>
              {resource.is_verified && (
                <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
              )}
            </div>
            {resource.subcategory && (
              <span className={`inline-block text-xs ${colors.text} ${colors.bg} px-2 py-0.5 rounded-full mb-2`}>
                {resource.subcategory}
              </span>
            )}
          </div>
          <ExternalLink className={`h-4 w-4 ${colors.text} flex-shrink-0 ml-2 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform`} />
        </div>

        <p className="text-sm text-gray-600 mb-3 leading-relaxed">
          {resource.description}
        </p>

        {showActions && (
          <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
            <button
              onClick={handleSave}
              className={`flex items-center gap-1 text-xs transition-colors ${
                isSaved ? colors.text : 'text-gray-500 hover:text-gray-700'
              }`}
              title="Save for later"
            >
              <Bookmark className={`h-3.5 w-3.5 ${isSaved ? 'fill-current' : ''}`} />
              {isSaved ? 'Saved' : 'Save'}
            </button>

            {isHelpful === null ? (
              <>
                <button
                  onClick={(e) => handleHelpful(true, e)}
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-green-600 transition-colors"
                  title="This was helpful"
                >
                  <ThumbsUp className="h-3.5 w-3.5" />
                  Helpful
                </button>
              </>
            ) : (
              <span className={`flex items-center gap-1 text-xs ${isHelpful ? 'text-green-600' : 'text-gray-400'}`}>
                <ThumbsUp className={`h-3.5 w-3.5 ${isHelpful ? 'fill-current' : ''}`} />
                {isHelpful ? 'Thanks!' : 'Feedback sent'}
              </span>
            )}

            {resource.quality_score >= 4 && (
              <span className="ml-auto text-xs text-gold-600 font-medium bg-gold-50 px-2 py-0.5 rounded-full">
                Top Resource
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
